import { api } from '@/api/apiClient';

/**
 * Fetch and render email template with variable substitution
 */
async function renderEmailTemplate(templateKey, variables) {
  try {
    const templates = await api.entities.EmailTemplate.filter({ 
      template_key: templateKey,
      is_active: true 
    });
    
    if (templates.length === 0) {
      throw new Error(`Email template not found: ${templateKey}`);
    }
    
    const template = templates[0];
    
    // Replace variables in subject and content
    let subject = template.subject;
    let htmlContent = template.body_html;
    
    Object.keys(variables).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      subject = subject.replace(regex, variables[key]);
      htmlContent = htmlContent.replace(regex, variables[key]);
    });
    
    return { subject, htmlContent };
  } catch (err) {
    console.error('Failed to render email template:', err);
    throw err;
  }
}

/**
 * Send email using the Core.SendEmail integration
 */
export async function sendEmail(to, templateKey, variables) {
  try {
    const { subject, htmlContent } = await renderEmailTemplate(templateKey, variables);
    
    await api.integrations.Core.SendEmail({
      to: to,
      subject: subject,
      body: htmlContent
    });
    
    return { success: true };
  } catch (err) {
    console.error('Failed to send email:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Send enrollment confirmation email
 */
export async function sendEnrollmentConfirmation(employee, enrollment, product, employer) {
  const variables = {
    employeeName: `${employee.first_name} ${employee.last_name}`,
    productName: product.name,
    coverageLevel: enrollment.coverage_level?.replace('_', ' ') || 'Employee Only',
    monthlyPremium: (enrollment.monthly_premium || 0).toFixed(2),
    coverageAmount: (enrollment.coverage_amount || 0).toLocaleString(),
    effectiveDate: enrollment.effective_date ? new Date(enrollment.effective_date).toLocaleDateString() : 'Pending',
    taxSavings: (enrollment.estimated_tax_savings || 0).toFixed(2),
    dashboardUrl: `${window.location.origin}/MyBenefits`,
    employerName: employer?.company_name || 'Your Benefits Team'
  };
  
  return sendEmail(employee.user_email, 'enrollment_confirmation', variables);
}

/**
 * Send flex proposal email
 */
export async function sendFlexProposal(employee, optimalFlex, annualSavings, employer) {
  const variables = {
    employeeName: `${employee.first_name} ${employee.last_name}`,
    optimalFlex: optimalFlex.toFixed(2),
    annualSavings: annualSavings.toFixed(2),
    monthlyBenefit: (annualSavings / 12).toFixed(2),
    enrollmentUrl: `${window.location.origin}/BenefitsCatalog`,
    employerName: employer?.company_name || 'Your Benefits Team'
  };
  
  return sendEmail(employee.user_email, 'flex_proposal', variables);
}

/**
 * Send QLE approval email
 */
export async function sendQLEApproval(employee, qle, employer) {
  const variables = {
    employeeName: `${employee.first_name} ${employee.last_name}`,
    eventType: qle.event_type?.replace('_', ' ') || 'Unknown',
    eventDate: qle.event_date ? new Date(qle.event_date).toLocaleDateString() : 'N/A',
    enrollmentStart: qle.special_enrollment_start ? new Date(qle.special_enrollment_start).toLocaleDateString() : 'N/A',
    enrollmentEnd: qle.special_enrollment_end ? new Date(qle.special_enrollment_end).toLocaleDateString() : 'N/A',
    enrollmentUrl: `${window.location.origin}/BenefitsCatalog`,
    employerName: employer?.company_name || 'Your Benefits Team'
  };
  
  return sendEmail(employee.user_email, 'qle_approved', variables);
}

/**
 * Send claim approval email
 */
export async function sendClaimApproval(employee, claim, employer) {
  const variables = {
    employeeName: `${employee.first_name} ${employee.last_name}`,
    claimAmount: (claim.claim_amount || 0).toFixed(2),
    claimType: claim.claim_type?.replace('_', ' ') || 'Unknown',
    claimDate: claim.claim_date ? new Date(claim.claim_date).toLocaleDateString() : 'N/A',
    approvedDate: claim.approved_date ? new Date(claim.approved_date).toLocaleDateString() : new Date().toLocaleDateString(),
    employerName: employer?.company_name || 'Your Benefits Team'
  };
  
  return sendEmail(employee.user_email, 'claim_approved', variables);
}

/**
 * Send employee invitation email
 */
export async function sendEmployeeInvitation(employee, employer, enrollmentDeadline = null) {
  const variables = {
    employeeName: `${employee.first_name} ${employee.last_name}`,
    employerName: employer?.company_name || 'Your Benefits Team',
    enrollmentUrl: `${window.location.origin}/Welcome`,
    enrollmentDeadline: enrollmentDeadline ? new Date(enrollmentDeadline).toLocaleDateString() : 'TBD',
    hrEmail: employer?.hr_contact_email || 'hr@company.com'
  };
  
  return sendEmail(employee.user_email, 'employee_invitation', variables);
}

/**
 * Send enrollment reminder email
 */
export async function sendEnrollmentReminder(employee, employer, enrollmentDeadline, daysRemaining) {
  const variables = {
    employeeName: `${employee.first_name} ${employee.last_name}`,
    daysRemaining: daysRemaining.toString(),
    enrollmentDeadline: new Date(enrollmentDeadline).toLocaleDateString(),
    enrollmentUrl: `${window.location.origin}/BenefitsCatalog`,
    employerName: employer?.company_name || 'Your Benefits Team'
  };
  
  return sendEmail(employee.user_email, 'enrollment_reminder', variables);
}